import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { Calendar } from "lucide-react";
import { ymdToDate, dateToYmd, fmtDisplayDate } from "./dateUtils";

const inputCls = "bg-[#0A0F1E] border-white/10 h-10 text-white";

// Reusable calendar-based date picker. Emits a "YYYY-MM-DD" string (or "").
// Implemented with a self-managed portal (instead of Radix Popover) so it works
// reliably INSIDE a modal Dialog — a modal Radix Dialog sets
// `pointer-events: none` on <body>, which can block clicks on portaled poppers.
// We render to document.body with explicit `pointerEvents: auto` and position
// the panel from the trigger's bounding rect.
export const WorkspaceDatePicker = ({ value, onChange, testid, placeholder = "Pick a date", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const selected = ymdToDate(value);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const calH = 380;
    const calW = 300;
    let top = r.bottom + 6;
    if (top + calH > window.innerHeight) top = Math.max(8, r.top - calH - 6);
    let left = r.left;
    if (left + calW > window.innerWidth) left = Math.max(8, window.innerWidth - calW - 8);
    setCoords({ top, left });
  }, []);

  const toggle = () => {
    if (!open) place();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onReposition = () => place();
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } };
    document.addEventListener("mousedown", onDocDown, true);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, place]);

  return (
    <div className={`relative ${className}`}>
      <Button
        ref={btnRef}
        type="button"
        variant="outline"
        className={`${inputCls} w-full justify-start text-left font-normal hover:bg-white/5 ${selected ? "" : "text-white/40"}`}
        onClick={toggle}
        data-testid={testid}
      >
        <Calendar size={14} className="mr-2 shrink-0 text-white/50" />
        <span className="truncate">{selected ? fmtDisplayDate(value) : placeholder}</span>
      </Button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 300, pointerEvents: "auto" }}
          className="rounded-md border border-white/10 bg-[#0E1424] text-white shadow-2xl"
          data-testid={testid ? `${testid}-popover` : undefined}
        >
          <CalendarUI
            mode="single"
            selected={selected}
            onSelect={(d) => { onChange(d ? dateToYmd(d) : ""); setOpen(false); }}
            initialFocus
          />
          {selected && (
            <div className="border-t border-white/10 p-2 flex justify-end">
              <Button variant="ghost" size="sm" className="h-7 text-white/60 hover:text-white"
                onClick={() => { onChange(""); setOpen(false); }} data-testid={testid ? `${testid}-clear` : undefined}>
                Clear
              </Button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default WorkspaceDatePicker;
